// The client portal's HTTP — its own, on purpose (docs/CLIENT_PORTAL.md §5).
//
// apps/shared/http.js adds `Authorization: Bearer <staff token>` whenever one
// is in localStorage. The portal must never send a staff credential: the
// server refuses it anyway, but a staff member who opens the portal on their
// own phone would be sending their token to routes that have no use for it.
// So this sends only the portal's cookie (same-origin, HttpOnly — script never
// sees it) and, on anything that changes state, the `X-FCP-Portal: 1` header
// the server's CSRF guard requires.
//
// Paths name their router (`/portal/me`), the way every apps/ call does, so
// apps/apiPaths.test.js can check them against server.js.

const API = '/api';

export class PortalError extends Error {
  constructor(status, body) {
    super((body && body.error) || (status === 0 ? 'Could not reach the server. Check your connection and try again.' : 'Something went wrong. Please try again.'));
    this.name = 'PortalError';
    this.status = status;
    this.body = body || null;
    this.code = (body && body.code) || null;
  }
}

async function request(method, path, body) {
  const headers = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-FCP-Portal'] = '1';
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (_) {
    throw new PortalError(0, null);
  }
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) throw new PortalError(res.status, data);
  // The server's idle clock restarts on every answered request; the shell's
  // "you will be signed out soon" follows it (PortalApp.js).
  try { window.dispatchEvent(new Event('portal:contact')); } catch (_) { /* no window in a test */ }
  return data;
}

export const get = (path) => request('GET', path);
export const post = (path, body) => request('POST', path, body === undefined ? {} : body);
export const del = (path) => request('DELETE', path);

/** A download link the browser follows itself, carrying the cookie (CSV exports, PDFs). */
export const downloadUrl = (path) => `${API}${path}`;

/** Whole cents → "$1,234.50". The portal is handed cents and never does arithmetic on dollars. */
export function money(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return (n / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** A company-local day ("2026-06-15") → "Jun 15, 2026". Read as a calendar day, never through a timezone. */
export function day(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  if (!m) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}
